import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useMemo } from 'react'
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import axiosInstance from '../utils/axios'
import { PATH_DASHBOARD } from '../routes/paths';
import errorHandling from '../utils/errorHandling';

RequestsProvider.propTypes = {
  children: PropTypes.node,
};

export const RequestsContext = createContext({})

export const useRequestsContext = () => {
  const context = useContext(RequestsContext);

  if (!context) throw new Error('useRequestsContext must be use inside RequestsProvider');

  return context;
};

export function RequestsProvider({ children }) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const getRequest = useCallback(async (id) => {
    try {
      const response = await axiosInstance.get(`api/v1/Solicitacao/${id}`)
      return response.data.data;
    } catch (error) {
      enqueueSnackbar(errorHandling(error), { variant: 'error' });
      return null;
    }
  }, [enqueueSnackbar])

  const getRequests = useCallback(async (id) => {
    try {
      let path = 'api/v1/Solicitacao/obter-todos/';
      if (id) path += id
      const response = await axiosInstance.get(path)
      return response.data.data;
    } catch (error) {
      enqueueSnackbar(errorHandling(error), { variant: 'error' });
      return null;
    }
  }, [enqueueSnackbar])

  const createRequest = useCallback(async (request) => {
    try {
      await axiosInstance.post('api/v1/Solicitacao', request)
      enqueueSnackbar('Solicitação cadastrada com sucesso!');
      navigate(PATH_DASHBOARD.request.list);
    } catch (error) {
      enqueueSnackbar(errorHandling(error), { variant: 'error' });
    }
  }, [enqueueSnackbar, navigate])

  const updateRequest = useCallback(async (request) => {
    try {
      await axiosInstance.put('api/v1/Solicitacao', request)
      enqueueSnackbar('Solicitação atualizada com sucesso!');
      navigate(PATH_DASHBOARD.request.list);
    } catch (error) {
      enqueueSnackbar(errorHandling(error), { variant: 'error' });
    }
  }, [enqueueSnackbar, navigate])

  const deleteRequest = useCallback(async (id) => {
    try {
      await axiosInstance.delete(`api/v1/Solicitacao/${id}`)
      enqueueSnackbar('Solicitação deletada com sucesso!');
    } catch (error) {
      enqueueSnackbar(errorHandling(error), { variant: 'error' });
    }
  }, [enqueueSnackbar])

  const getDependents = useCallback(async (id) => {
    try {
      const response = await axiosInstance.get(`api/v1/Usuario/dependentes/${id}`)
      return response.data.data;
    } catch (error) {
      enqueueSnackbar(errorHandling(error), { variant: 'error' });
      return null;
    }
  }, [enqueueSnackbar])

  const value = useMemo(
    () => ({
      getRequest,
      getRequests,
      createRequest,
      updateRequest,
      deleteRequest,
      getDependents,
    }), [
      getRequest, 
      getRequests,
      createRequest, 
      updateRequest, 
      deleteRequest,
      getDependents,
    ]);

  return (
    <RequestsContext.Provider
      value={value}
    >
      {children}
    </RequestsContext.Provider>
  )
}
